import { QueryKey } from '@fabric-ui/utils';
import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { z } from 'zod';
import { useAvContext } from '../../context/AvContextProvider';
import { isFirstPartySourceType } from '../Sources/utils';

const source = z
  .object({
    name: z.string(),
    mappedSourceName: z.string(),
    id: z.string(),
    dataSourceType: z.string(),
    active: z.boolean(),
    redactedConfig: z
      .object({
        viewConfig: z.object({
          builtinProjId: z.object({
            name: z.string(),
            builtIn: z.boolean(),
          }),
        }),
      })
      .partial()
      .nullable(),
  })
  .transform(raw => ({
    ...raw,
    isFirstParty: isFirstPartySourceType(raw.dataSourceType),
    projectionIdName: raw.redactedConfig?.viewConfig?.builtinProjId?.name || null,
  }));

export const useGetSourcesList = () => {
  const { api, typeNameMap } = useAvContext();
  const sourcesMapByName = typeNameMap?.sourcesObj?.sourcesMapByName;
  return useQuery({
    enabled: !!sourcesMapByName,
    queryKey: [QueryKey.GETSOURCESLIST],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      api(GET_SOURCES_LIST, { signal, onSuccess: ({ data }) => data.findDataSourceInstancesByAccountId }),
    select: data =>
      source
        .array()
        .parse(data)
        .map(source => ({ ...source, iconUrl: z.string().optional().parse(sourcesMapByName[source.mappedSourceName]?.iconUrl) })),
  });
};

export type Source = NonNullable<ReturnType<typeof useGetSourcesList>['data']>[number];

const GET_SOURCES_LIST = gql`
  query findDataSourceInstancesByAccountId {
    findDataSourceInstancesByAccountId {
      id
      name
      mappedSourceName
      dataSourceType
      redactedConfig
      active
    }
  }
`;
